/**
 * Canonical cents ↔ dollars boundary for application code.
 * Cents are integer-valued at persistence/API boundaries; dollars are
 * floating UI amounts. Cross the boundary only through these helpers.
 */

export type Cents = number & { readonly __brand: "Cents" };
export type Dollars = number & { readonly __brand: "Dollars" };

function assertFiniteIntegerCents(value: number): void {
  if (!Number.isFinite(value)) {
    throw new TypeError("cents must be a finite number");
  }
  if (!Number.isInteger(value)) {
    throw new TypeError("cents must be an integer");
  }
}

function assertFiniteDollars(value: number): void {
  if (!Number.isFinite(value)) {
    throw new TypeError("dollars must be a finite number");
  }
}

/**
 * Converts an integer cent amount to branded dollars (numeric dollars, not a display string).
 */
export function toDollars(cents: Cents): Dollars {
  assertFiniteIntegerCents(cents);
  const dollars = cents / 100;
  return dollars as Dollars;
}

/**
 * Converts branded dollars to integer cents (rounded half-up via Math.round).
 */
export function toCents(dollars: Dollars): Cents {
  assertFiniteDollars(dollars);
  const cents = Math.round(dollars * 100);
  assertFiniteIntegerCents(cents);
  return cents as Cents;
}

/**
 * Two-decimal diagnostic string for logs and server-side warnings only.
 * UI surfaces should use presentation helpers; this lives in the canonical
 * money module so `check:money` does not flag `.toFixed(2)` in business logic.
 */
export function formatMoneyDiagnosticDollars(value: number): string {
  if (!Number.isFinite(value)) {
    return String(value);
  }
  return (Math.round(value * 100) / 100).toFixed(2);
}
